import React from "react";
import { TextField, Checkbox, FormControlLabel } from "@mui/material";
import { format } from "date-fns";
import type { AdditionalFieldType } from "@/types/additionalFields";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";

interface RenderAdditionalFieldInputProps {
  fieldType: AdditionalFieldType;
  value: string | number;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  multilineRows?: number;
  maxLength?: number;
  helperText?: string;
  sx?: Record<string, unknown>;
  InputLabelProps?: Record<string, unknown>;
}

/**
 * field_typeに応じた入力フィールドをレンダリングする共通ユーティリティ関数
 *
 * React Hook Formを使用しない場合の入力フィールドレンダリングに使用します。
 * 状態管理は呼び出し側で`value`と`onChange`プロップを通じて行います。
 *
 * @remarks
 * React Hook Formと統合する場合は、代わりに`AdditionalFieldInput`コンポーネントを使用してください。
 * このユーティリティ関数は、EditableCell（インライン編集）やMapPlotRequestFilterModal（フィルター入力）など、
 * フォーム外での使用を想定しています。
 *
 * @param fieldType - フィールドタイプ（text, textarea, number, date, datetime, boolean）
 * @param value - 現在の値
 * @param onChange - 値変更時のコールバック
 * @param onKeyDown - キーダウンイベントハンドラ（オプション）
 * @param label - フィールドのラベル（オプション）
 * @param placeholder - プレースホルダーテキスト（オプション）
 * @param disabled - 入力を無効化するかどうか（デフォルト: false）
 * @param autoFocus - 自動フォーカスするかどうか（デフォルト: false）
 * @param size - 入力フィールドのサイズ（デフォルト: "medium"）
 * @param fullWidth - 幅を100%にするかどうか（デフォルト: true）
 * @param multilineRows - textareaの行数（デフォルト: 3）
 * @param maxLength - 最大文字数制限（オプション）
 * @param helperText - ヘルパーテキスト（オプション）
 * @param sx - MUIのsxプロップ（オプション）
 * @param InputLabelProps - TextFieldのInputLabelPropsプロップ（オプション）
 *
 * @returns field_typeに応じた入力フィールドのJSX要素
 *
 * @see {@link AdditionalFieldInput} - React Hook Form統合版コンポーネント
 *
 * @example
 * // インライン編集での使用例
 * renderAdditionalFieldInput({
 *   fieldType: "text",
 *   value: editValue,
 *   onChange: setEditValue,
 *   label: "メールアドレス",
 *   autoFocus: true,
 * })
 *
 * @example
 * // フィルター入力での使用例
 * renderAdditionalFieldInput({
 *   fieldType: "number",
 *   value: filterValue,
 *   onChange: (newValue) => setFilters({ ...filters, price: newValue }),
 *   label: "価格",
 *   placeholder: "価格を入力",
 * })
 */
export const renderAdditionalFieldInput = ({
  fieldType,
  value,
  onChange,
  onKeyDown,
  label,
  placeholder,
  disabled = false,
  autoFocus = false,
  size = "medium",
  fullWidth = true,
  multilineRows = 3,
  maxLength,
  helperText,
  sx,
  InputLabelProps,
}: RenderAdditionalFieldInputProps): JSX.Element => {
  const stringValue = String(value ?? "");

  switch (fieldType) {
    case "boolean":
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={stringValue === "true"}
              onChange={(e) => {
                onChange(e.target.checked ? "true" : "false");
              }}
              disabled={disabled}
            />
          }
          label={label ?? ""}
        />
      );

    case "number":
      return (
        <TextField
          value={stringValue}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onKeyDown={(e) => {
            onKeyDown?.(e);
            preventNonNumericValueWhenKeyDown(e);
          }}
          onPaste={preventNonNumericValueWhenPaste}
          type="number"
          label={label}
          placeholder={placeholder}
          size={size}
          autoFocus={autoFocus}
          fullWidth={fullWidth}
          disabled={disabled}
          inputProps={{
            // step="any"により小数点入力を許可
            step: "any",
          }}
          helperText={helperText}
          sx={sx}
          InputLabelProps={InputLabelProps}
        />
      );

    case "date":
      return (
        <TextField
          value={stringValue ? format(new Date(stringValue), "yyyy-MM-dd") : ""}
          onChange={(e) => {
            onChange(e.target.value || "");
          }}
          onKeyDown={onKeyDown}
          type="date"
          label={label}
          size={size}
          autoFocus={autoFocus}
          fullWidth={fullWidth}
          disabled={disabled}
          InputLabelProps={{
            shrink: true,
            ...InputLabelProps,
          }}
          sx={sx}
        />
      );

    case "datetime":
      return (
        <TextField
          value={
            stringValue
              ? format(new Date(stringValue), "yyyy-MM-dd'T'HH:mm")
              : ""
          }
          onChange={(e) => {
            onChange(e.target.value || "");
          }}
          onKeyDown={onKeyDown}
          type="datetime-local"
          label={label}
          size={size}
          autoFocus={autoFocus}
          fullWidth={fullWidth}
          disabled={disabled}
          InputLabelProps={{
            shrink: true,
            ...InputLabelProps,
          }}
          sx={sx}
        />
      );

    case "textarea":
      return (
        <TextField
          value={stringValue}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onKeyDown={onKeyDown}
          type="text"
          label={label}
          placeholder={placeholder}
          size={size}
          autoFocus={autoFocus}
          fullWidth={fullWidth}
          disabled={disabled}
          multiline
          rows={multilineRows}
          inputProps={maxLength ? { maxLength } : undefined}
          helperText={helperText}
          sx={sx}
          InputLabelProps={InputLabelProps}
        />
      );

    case "text":
    default:
      return (
        <TextField
          value={stringValue}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onKeyDown={onKeyDown}
          type="text"
          label={label}
          placeholder={placeholder}
          size={size}
          autoFocus={autoFocus}
          fullWidth={fullWidth}
          disabled={disabled}
          inputProps={maxLength ? { maxLength } : undefined}
          helperText={helperText}
          sx={sx}
          InputLabelProps={InputLabelProps}
        />
      );
  }
};
